import type { ColumnDef } from '@tanstack/react-table'
import { Pencil, Trash2, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { memo, ReactNode } from 'react'

import { Button, ButtonProps, SimpleTooltip } from '@fp/ui-kit'
import { ConfirmActionButton } from '@ui/ConfirmActionButton'

export type TableAction = {
  key: string
  content: ReactNode
  href?: string
  tooltip?: string
}

type TableActionsProps = {
  /** Falsy entries are skipped, so a permission-gated action reads `canEdit && createEditAction(...)`. */
  actions: Array<TableAction | false | null | undefined>
  className?: string
}

export const TableActions = memo(({ actions, className }: TableActionsProps) => {
  return (
    <div className={`flex w-fit gap-0.5 ${className || ''}`}>
      {actions
        .filter((action): action is TableAction => !!action)
        .map((action) => {
          const content = action.tooltip ? (
            <SimpleTooltip content={action.tooltip}>{action.content}</SimpleTooltip>
          ) : (
            action.content
          )

          return action.href ? (
            <Link key={action.key} href={action.href}>
              {content}
            </Link>
          ) : (
            <div key={action.key}>{content}</div>
          )
        })}
    </div>
  )
})

TableActions.displayName = 'TableActions'

export const TABLE_ACTION_LABELS = { view: 'View details', edit: 'Edit', delete: 'Delete' } as const

const defaultButtonProps: ButtonProps = {
  variant: 'outline',
  size: 'sm',
}

export const createViewAction = (href: string, buttonProps?: ButtonProps): TableAction => ({
  key: 'view',
  href,
  tooltip: TABLE_ACTION_LABELS.view,
  content: (
    <Button aria-label={TABLE_ACTION_LABELS.view} {...defaultButtonProps} {...buttonProps}>
      <ChevronRight />
    </Button>
  ),
})

export const createEditAction = (onClick: () => void, buttonProps?: ButtonProps): TableAction => ({
  key: 'edit',
  tooltip: TABLE_ACTION_LABELS.edit,
  content: (
    <Button aria-label={TABLE_ACTION_LABELS.edit} {...defaultButtonProps} {...buttonProps} onClick={onClick}>
      <Pencil />
    </Button>
  ),
})

export const createShowAction = (onClick: () => void, buttonProps?: ButtonProps): TableAction => ({
  key: 'show',
  tooltip: TABLE_ACTION_LABELS.view,
  content: (
    <Button aria-label={TABLE_ACTION_LABELS.view} {...defaultButtonProps} {...buttonProps} onClick={onClick}>
      <ChevronRight />
    </Button>
  ),
})

export const createDeleteAction = (
  itemName: string,
  onConfirm: () => void,
  buttonProps?: ButtonProps,
): TableAction => ({
  key: 'delete',
  content: (
    <ConfirmActionButton
      title='Delete Item'
      button={
        <Button aria-label={TABLE_ACTION_LABELS.delete} {...defaultButtonProps} {...buttonProps}>
          <Trash2 />
        </Button>
      }
      message={`Are you sure you want to delete ${itemName}?`}
      onConfirm={onConfirm}
      tooltipText={TABLE_ACTION_LABELS.delete}
    />
  ),
})

export const createCustomAction = (
  key: string,
  content: ReactNode,
  options?: {
    href?: string
    tooltip?: string
  },
): TableAction => ({
  key,
  content,
  ...options,
})

/** A column that only exists when `show` is truthy (e.g. an actions column with no permitted actions). Spread it. */
export const optionalColumn = <T,>(show: unknown, column: ColumnDef<T>): ColumnDef<T>[] => (show ? [column] : [])
